import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getStaffPayments } from "@/lib/staff-api";
import type { PaymentRow } from "@/lib/staff-api";
import { STAFF_STALE_TIME_MS, staffQueryKeys } from "@/lib/staff-hooks";
import { formatCurrency, formatDate, formatOrderStatus } from "@/lib/formatters";
import { defaultDayRange, downloadCsv, paymentsCsv, rangeToInstants, UNKNOWN_VENDOR } from "@/lib/staff-payments";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { SECONDARY_BUTTON } from "./buttons";

/**
 * Accounting's read-only payment list: a date-range filter, the table, and a
 * client-side CSV export. The export adds per-product columns (BCB and Badge
 * quantity and subtotal, blank without order lines) so a mixed payment can be
 * journal-split. Accounting's import format is still an open question (spec
 * open question 4): Celeste and Becky may redefine the CSV columns later, in
 * PAYMENT_CSV_COLUMNS (lib/staff-payments.ts).
 */

const INPUT = "rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900";
const TH = "px-3 py-2 text-left font-medium text-gray-700";
const TD = "px-3 py-2 text-gray-900";

function PaymentsTable({ payments }: { payments: PaymentRow[] }): React.ReactElement {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {["Date", "Vendor", "Order type", "Amount", "Vanco payment ID", "Card last 4", "Status", "Flagged"].map(
              (h) => (
                <th key={h} scope="col" className={TH}>
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {payments.map((p) => (
            <tr key={p.orderId}>
              <td className={TD}>{formatDate(p.date)}</td>
              <td className={TD}>
                {p.vendorCompanyName ?? UNKNOWN_VENDOR}
                {p.vendorHidden && (
                  <span className="ml-2 rounded-full bg-gray-700 px-2 py-0.5 text-xs font-medium text-white">Hidden</span>
                )}
              </td>
              <td className={TD}>{p.orderTypeLabel}</td>
              <td className={TD}>{formatCurrency(p.amount)}</td>
              <td className={TD}>{p.vancoPaymentID}</td>
              <td className={TD}>{p.cardLast4}</td>
              <td className={TD}>{formatOrderStatus(p.status)}</td>
              <td className={TD}>{p.reconcileNeeded ? "Yes" : "No"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StaffPaymentsPage(): React.ReactElement {
  const [{ start: initialStart, end: initialEnd }] = useState(() => defaultDayRange());
  const [startDay, setStartDay] = useState(initialStart);
  const [endDay, setEndDay] = useState(initialEnd);
  const startId = useId();
  const endId = useId();

  const range = rangeToInstants(startDay, endDay);
  const query = useQuery({
    queryKey: staffQueryKeys.payments(range?.start ?? "", range?.end ?? ""),
    queryFn: () => getStaffPayments(range!.start, range!.end),
    enabled: range !== null,
    staleTime: STAFF_STALE_TIME_MS,
  });
  const payments = range ? (query.data?.payments ?? null) : null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Payments</h1>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor={startId} className="block text-sm font-medium text-gray-700">
            From
          </label>
          <input
            id={startId}
            type="date"
            value={startDay}
            onChange={(e) => setStartDay(e.target.value)}
            className={INPUT}
          />
        </div>
        <div>
          <label htmlFor={endId} className="block text-sm font-medium text-gray-700">
            To
          </label>
          <input id={endId} type="date" value={endDay} onChange={(e) => setEndDay(e.target.value)} className={INPUT} />
        </div>
        <button
          type="button"
          disabled={!payments || payments.length === 0}
          onClick={() => payments && downloadCsv(`payments-${startDay}-to-${endDay}.csv`, paymentsCsv(payments))}
          className={SECONDARY_BUTTON}
        >
          Export CSV
        </button>
      </div>

      {!range && (
        <p role="alert" className="text-sm text-red-700">
          Choose a start date on or before the end date.
        </p>
      )}
      {range && query.isError && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          <span>This list could not be loaded.</span>
          <button type="button" onClick={() => void query.refetch()} className={SECONDARY_BUTTON}>
            Try again
          </button>
        </div>
      )}
      {range && query.isPending && <LoadingSpinner />}
      {payments && payments.length === 0 && <p className="text-sm text-gray-600">No payments in this date range.</p>}
      {payments && payments.length > 0 && <PaymentsTable payments={payments} />}
    </div>
  );
}
